import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Slider } from '@/components/ui/slider';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Calculator, Zap, TrendingUp, Lock } from 'lucide-react';

const ROICalculator = () => {
  const investmentAmount = 25;
  const [numberOfMachines, setNumberOfMachines] = useState(10);
  const [revenuePerMachine, setRevenuePerMachine] = useState(100000);
  const [revenueShare, setRevenueShare] = useState(10);
  const [performanceBonus, setPerformanceBonus] = useState(2);

  const applyPreset = (preset) => {
    if (preset === 'conservative') {
      setRevenueShare(8);
      setPerformanceBonus(1);
      setRevenuePerMachine(80000);
    } else if (preset === 'optimistic') {
      setRevenueShare(10);
      setPerformanceBonus(2);
      setRevenuePerMachine(150000);
    }
  };

  const formatNumber = (num) => {
    return num.toLocaleString('en-IN', { maximumFractionDigits: 0 });
  };

  const calculateReturns = () => {
    const totalMonthlyRevenue = numberOfMachines * revenuePerMachine;
    const totalReturnPercentage = revenueShare + performanceBonus;
    const monthlyEarnings = (totalMonthlyRevenue * totalReturnPercentage) / 100;
    const yearlyEarnings = monthlyEarnings * 12;
    
    return {
      totalMonthlyRevenue: formatNumber(totalMonthlyRevenue),
      totalReturnPercentage,
      monthlyEarnings: formatNumber(monthlyEarnings),
      yearlyEarnings: formatNumber(yearlyEarnings)
    };
  };

  const returns = calculateReturns();

  const scrollToContact = () => {
    const element = document.getElementById('contact');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="calculator" className="py-24 md:py-32 bg-[#F3F0E6]">
      <div className="container mx-auto px-6 md:px-12 max-w-7xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="text-sm uppercase tracking-[0.2em] font-bold text-[#10B981]">Investor Return Calculator</span>
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-[#022C22] mt-4 mb-4">
            Estimate Your Returns from Smart Vending Investment
          </h2>
          <p className="text-base leading-relaxed text-[#4B5563] max-w-2xl mx-auto font-medium">
            Customize your investment parameters to see potential returns
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6" data-testid="roi-calculator">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="lg:col-span-2 bg-gradient-to-br from-white to-[#F3F0E6] border-2 border-[#10B981] rounded-3xl shadow-xl p-8 md:p-10"
          >
            <div className="flex items-center justify-between mb-8">
              <div className="flex items-center space-x-3">
                <div className="bg-[#10B981] p-3 rounded-2xl">
                  <Calculator className="h-6 w-6 text-white" />
                </div>
                <h3 className="text-2xl font-bold tracking-tight text-[#022C22]">Investment Parameters</h3>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 border-2 border-[#E5E7EB]">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center space-x-2">
                    <Lock className="h-4 w-4 text-[#4B5563]" />
                    <label className="text-sm font-bold text-[#064E3B] uppercase tracking-wide">Investment Amount</label>
                  </div>
                </div>
                <p className="text-5xl font-black text-[#022C22] mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>₹{investmentAmount}L</p>
                <p className="text-xs font-semibold text-[#4B5563] uppercase tracking-wide">Fixed Investment</p>
              </div>

              <div className="bg-gradient-to-br from-[#EDE9FE] to-[#DDD6FE] rounded-2xl p-6 border-2 border-[#7C3AED] shadow-lg">
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-bold text-[#5B21B6] uppercase tracking-wide">Number of Machines</label>
                </div>
                <div className="flex items-center gap-4 mb-4">
                  <Input
                    type="number"
                    value={numberOfMachines}
                    onChange={(e) => {
                      const val = Math.min(100, Math.max(10, Number(e.target.value)));
                      setNumberOfMachines(val);
                    }}
                    className="w-24 text-3xl font-black text-center border-2 border-[#7C3AED] h-16 bg-white text-[#5B21B6]"
                    min={10}
                    max={100}
                  />
                  <span className="text-sm font-bold text-[#5B21B6]">machines</span>
                </div>
                <Slider
                  value={[numberOfMachines]}
                  onValueChange={(value) => setNumberOfMachines(value[0])}
                  min={10}
                  max={100}
                  step={5}
                  className="w-full [&_[role=slider]]:h-6 [&_[role=slider]]:w-6 [&_[role=slider]]:bg-[#7C3AED] [&_[role=slider]]:border-4 [&_[role=slider]]:border-white [&_[role=slider]]:shadow-xl [&_span:first-child_>_span]:bg-[#7C3AED]/30 [&_span:first-child_>_span_>_span]:bg-[#7C3AED]"
                />
                <div className="flex justify-between text-xs font-bold text-[#5B21B6] mt-3 uppercase">
                  <span>10</span>
                  <span>50</span>
                  <span>100</span>
                </div>
              </div>

              <div className="bg-gradient-to-br from-[#D1FAE5] to-[#A7F3D0] rounded-2xl p-6 border-2 border-[#10B981] shadow-lg">
                <label htmlFor="revenue-per-machine" className="text-sm font-bold text-[#064E3B] block mb-4 uppercase tracking-wide">
                  Monthly Revenue per Machine
                </label>
                <Input
                  id="revenue-per-machine"
                  type="number"
                  value={revenuePerMachine}
                  onChange={(e) => setRevenuePerMachine(Number(e.target.value))}
                  className="text-2xl font-black border-2 border-[#059669] focus:border-[#10B981] h-14 bg-white"
                  min={50000}
                  max={200000}
                />
                <p className="text-xs font-semibold text-[#064E3B] mt-2 uppercase tracking-wide">Adjust based on location</p>
              </div>

              <div className="bg-gradient-to-br from-[#DBEAFE] to-[#BFDBFE] rounded-2xl p-6 border-2 border-[#3B82F6] shadow-lg">
                <div className="flex justify-between items-center mb-4">
                  <label className="text-sm font-bold text-[#1E40AF] uppercase tracking-wide">Revenue Share (%)</label>
                  <span className="text-3xl font-black text-[#1E40AF] data-font">{revenueShare}%</span>
                </div>
                <Slider
                  value={[revenueShare]}
                  onValueChange={(value) => setRevenueShare(value[0])}
                  min={8}
                  max={10}
                  step={0.5}
                  className="w-full"
                />
                <div className="flex justify-between text-xs font-bold text-[#1E40AF] mt-3 uppercase">
                  <span>8%</span>
                  <span>10%</span>
                </div>
              </div>

              <div className="bg-gradient-to-br from-[#FEF3C7] to-[#FDE68A] rounded-2xl p-6 border-2 border-[#F59E0B] shadow-lg">
                <div className="flex justify-between items-center mb-4">
                  <label className="text-sm font-bold text-[#92400E] uppercase tracking-wide">Performance Bonus (%)</label>
                  <span className="text-3xl font-black text-[#92400E] data-font">{performanceBonus}%</span>
                </div>
                <Slider
                  value={[performanceBonus]}
                  onValueChange={(value) => setPerformanceBonus(value[0])}
                  min={1}
                  max={2}
                  step={0.5}
                  className="w-full"
                />
                <div className="flex justify-between text-xs font-bold text-[#92400E] mt-3 uppercase">
                  <span>1%</span>
                  <span>2%</span>
                </div>
              </div>

              <div className="bg-gradient-to-br from-[#10B981] to-[#059669] rounded-2xl p-6 shadow-xl border-2 border-[#047857]">
                <p className="text-sm font-bold text-white mb-3 uppercase tracking-wide">Total Return</p>
                <p className="text-5xl font-black text-white data-font mb-2">{returns.totalReturnPercentage}%</p>
                <p className="text-xs font-bold text-[#D1FAE5] uppercase tracking-wide">Revenue Share + Bonus</p>
              </div>
            </div>

            <div className="mt-8 flex flex-wrap gap-4">
              <Button
                onClick={() => applyPreset('conservative')}
                variant="outline"
                className="border-2 border-[#4B5563] text-[#4B5563] hover:bg-[#4B5563] hover:text-white font-bold px-6 py-3 text-base"
              >
                Conservative (8% + 1%)
              </Button>
              <Button
                onClick={() => applyPreset('optimistic')}
                variant="outline"
                className="border-2 border-[#10B981] text-[#10B981] hover:bg-[#10B981] hover:text-white font-bold px-6 py-3 text-base"
              >
                Optimistic (10% + 2%)
              </Button>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="space-y-6"
          >
            <div className="bg-white border-2 border-[#E5E7EB] rounded-2xl shadow-md p-6">
              <div className="flex items-center space-x-2 mb-4">
                <TrendingUp className="h-6 w-6 text-[#4B5563]" />
                <p className="text-xs font-bold text-[#4B5563] uppercase tracking-wider">Total Monthly Revenue</p>
              </div>
              <p className="text-4xl font-black text-[#022C22] data-font mb-2">₹{returns.totalMonthlyRevenue}</p>
              <p className="text-sm font-bold text-[#4B5563]">From {numberOfMachines} machines</p>
            </div>

            <div className="bg-gradient-to-br from-[#D1FAE5] to-[#A7F3D0] border-2 border-[#10B981] rounded-2xl shadow-xl p-6">
              <div className="flex items-center space-x-2 mb-4">
                <Zap className="h-6 w-6 text-[#10B981]" />
                <p className="text-xs font-bold text-[#064E3B] uppercase tracking-wider">Monthly Investor Earnings</p>
              </div>
              <p className="text-5xl font-black text-[#10B981] data-font mb-2">₹{returns.monthlyEarnings}</p>
              <p className="text-sm font-bold text-[#064E3B]">at {returns.totalReturnPercentage}% total return</p>
            </div>

            <div className="bg-gradient-to-br from-[#064E3B] to-[#022C22] text-white rounded-2xl shadow-2xl p-6 border-2 border-[#10B981]">
              <p className="text-xs font-bold text-[#D1FAE5] uppercase tracking-wider mb-4">Total Yearly Earnings</p>
              <p className="text-5xl font-black text-[#F59E0B] data-font mb-3">₹{returns.yearlyEarnings}</p>
              <p className="text-sm font-bold text-[#D1FAE5] mb-4">Over 12 months</p>
              <p className="text-xs font-semibold text-[#F3F0E6] italic bg-white/10 p-3 rounded-lg border border-white/20">
                * Returns may vary based on machine performance and location. All calculations exclusive of taxes.
              </p>
            </div>

            <Button
              onClick={scrollToContact}
              className="w-full bg-gradient-to-r from-[#10B981] to-[#059669] hover:from-[#059669] hover:to-[#047857] text-white font-black py-7 text-lg shadow-2xl border-2 border-[#047857] rounded-xl"
            >
              Request Detailed Proposal
            </Button>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="bg-gradient-to-r from-[#D1FAE5] to-[#FEF3C7] border-l-4 border-[#10B981] rounded-xl p-6 text-center shadow-lg"
        >
          <p className="text-lg font-black text-[#022C22] italic">
            "Tap into India's daily consumption economy - powered by a scalable vending network"
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export default ROICalculator;
